import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

interface VoteCompletionScreenProps {
  voterName?: string;
  onBack: () => void;
  onViewResults?: () => void;
  onEditVote?: () => void;
  title?: string;
  subtitle?: string;
  color?: "green" | "blue" | "violet" | "emerald";
  onSendEmail?: (email: string) => Promise<void>;
  pollSlug?: string;
}

const VoteCompletionScreen: React.FC<VoteCompletionScreenProps> = ({
  voterName,
  onBack,
  onViewResults,
  onEditVote,
  title,
  subtitle,
  color = "blue", // Changé de "green" à "blue" pour mieux correspondre au thème
  onSendEmail,
  pollSlug,
}) => {
  const { t } = useTranslation("polls");
  const resolvedVoterName = voterName ?? t("voteCompletion.defaultVoterName");
  const resolvedTitle = title ?? t("voteCompletion.title");
  const [email, setEmail] = React.useState("");
  const [emailSent, setEmailSent] = React.useState(false);
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);

  const colorMap = {
    green: {
      bg: "bg-green-100 dark:bg-green-900/20",
      text: "text-green-600 dark:text-green-500",
      button: "bg-green-600 hover:bg-green-700",
      alertBg: "bg-green-50 dark:bg-green-900/10",
      alertBorder: "border-green-200 dark:border-green-900/30",
      alertText: "text-green-700 dark:text-green-400",
      alertTextLight: "text-green-600 dark:text-green-300",
      outlineButton:
        "text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20",
    },
    blue: {
      bg: "bg-blue-500/20 dark:bg-blue-400/20",
      text: "text-blue-600 dark:text-blue-400",
      button: "bg-blue-600 hover:bg-blue-700",
      alertBg: "bg-blue-500/10 dark:bg-blue-900/20",
      alertBorder: "border-blue-200 dark:border-blue-700",
      alertText: "text-blue-700 dark:text-blue-300",
      alertTextLight: "text-blue-600 dark:text-blue-400",
      outlineButton:
        "text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20",
    },
    violet: {
      bg: "bg-violet-100 dark:bg-violet-900/20",
      text: "text-violet-600 dark:text-violet-400",
      button: "bg-violet-600 hover:bg-violet-700",
      alertBg: "bg-violet-50 dark:bg-violet-900/10",
      alertBorder: "border-violet-200 dark:border-violet-900/30",
      alertText: "text-violet-700 dark:text-violet-400",
      alertTextLight: "text-violet-600 dark:text-violet-300",
      outlineButton:
        "text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-900/20",
    },
    emerald: {
      bg: "bg-emerald-100 dark:bg-emerald-900/20",
      text: "text-emerald-600 dark:text-emerald-500",
      button: "bg-emerald-600 hover:bg-emerald-700",
      alertBg: "bg-emerald-50 dark:bg-emerald-900/10",
      alertBorder: "border-emerald-200 dark:border-emerald-900/30",
      alertText: "text-emerald-700 dark:text-emerald-400",
      alertTextLight: "text-emerald-600 dark:text-emerald-300",
      outlineButton:
        "text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
    },
  };

  const theme = colorMap[color];

  return (
    <div className="min-h-screen-safe bg-[#0a0a0a]">
      <div className="flex items-center justify-center p-4 min-h-[calc(100dvh-80px)]">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center w-full max-w-md"
        >
          <div className="bg-[#1e1e1e] rounded-2xl p-8 shadow-sm border border-gray-700">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className={`w-20 h-20 ${theme.bg} rounded-full flex items-center justify-center mx-auto mb-6`}
            >
              <Check className={`w-10 h-10 ${theme.text}`} />
            </motion.div>
            <h2 className="text-2xl font-bold mb-2 text-white">
              {resolvedTitle}
            </h2>
            <p className="text-gray-400 mb-8">
              {subtitle ||
                t("voteCompletion.subtitle", { name: resolvedVoterName })}
            </p>

            {/* Message d'information pour la bêta */}
            {/* <div
              className={`mb-8 p-4 ${theme.alertBg} border ${theme.alertBorder} rounded-lg text-left`}
            >
              <div className={`flex items-center gap-2 ${theme.alertText}`}>
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium">Information bêta</span>
              </div>
              <p className={`text-sm ${theme.alertTextLight} mt-1`}>
                Pour finaliser et partager votre sondage, après la bêta, vous devrez vous connecter
                ou créer un compte.
              </p>
            </div> */}

            <div className="flex flex-col gap-3 items-center">
              {onSendEmail && !emailSent && (
                <div className="w-full mb-4 pt-4 border-t border-gray-700/50">
                  <p className="text-xs text-gray-400 mb-3">
                    {t("voteCompletion.emailCopyPrompt")}
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder={t("voteCompletion.emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 px-3 py-2 bg-[#2a2a2a] border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                    />
                    <button
                      onClick={async () => {
                        if (!email || !email.includes("@")) {
                          setEmailError(t("voteCompletion.invalidEmail"));
                          return;
                        }
                        setSendingEmail(true);
                        setEmailError(null);
                        try {
                          await onSendEmail(email);
                          setEmailSent(true);
                        } catch (err) {
                          setEmailError(t("voteCompletion.sendError"));
                        } finally {
                          setSendingEmail(false);
                        }
                      }}
                      disabled={sendingEmail || !email}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${theme.button} text-white disabled:opacity-50`}
                    >
                      {sendingEmail ? "..." : t("voteCompletion.sendButton")}
                    </button>
                  </div>
                  {emailError && (
                    <p className="text-xs text-red-400 mt-1">{emailError}</p>
                  )}
                </div>
              )}

              {emailSent && (
                <div className="w-full mb-4 pt-4 border-t border-gray-700/50">
                  <p className="text-sm text-green-400 flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    {t("voteCompletion.emailSent")}
                  </p>
                </div>
              )}

              {onViewResults && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={onViewResults}
                  className={`bg-[#1e1e1e] font-medium py-3 px-6 rounded-xl w-full transition-colors border ${theme.outlineButton}`}
                >
                  {t("voteCompletion.viewResults")}
                </motion.button>
              )}

              {onEditVote && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={onEditVote}
                  className="bg-[#2a2a2a] hover:bg-[#333333] font-medium py-3 px-6 rounded-xl w-full transition-colors border border-gray-600 text-gray-300"
                >
                  {t("voteCompletion.editResponses")}
                </motion.button>
              )}

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className={`${
                  onViewResults
                    ? `${theme.button} text-white`
                    : `${theme.button} text-white`
                } font-medium py-3 px-6 rounded-xl w-full transition-colors shadow-sm`}
              >
                {t("voteCompletion.backHome")}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VoteCompletionScreen;
